import React, { useEffect, useId, useState } from 'react';

import styles from './styles.module.scss';

const TINT_COLOR = '#1ba1e0';
const SAMPLE_SIZE = 24;
const WHITE_LUMINANCE_THRESHOLD = 235;
const TRANSPARENT_ALPHA_THRESHOLD = 16;
// Catches a logo that's only partly white (e.g. a colored icon plus white wordmark text) — the
// icon drags the whole-image average below WHITE_LUMINANCE_THRESHOLD even though the white text
// portion is just as invisible against a white header as a fully-white logo would be.
const NEAR_WHITE_PIXEL_FRACTION_THRESHOLD = 0.15;

/** Samples the loaded logo on an offscreen canvas to check whether it's near-white — or has a
 * significant near-white portion — and would therefore vanish (in part or whole) against a white
 * header. Fails closed to `false` (keep original color) on any load/read error, including a
 * cross-origin canvas taint. */
function useIsNearWhiteLogo(src: string | null | undefined, skip: boolean): boolean {
  const [isNearWhite, setIsNearWhite] = useState(false);

  useEffect(() => {
    if (!src || skip) {
      setIsNearWhite(false);
      return undefined;
    }

    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = SAMPLE_SIZE;
        canvas.height = SAMPLE_SIZE;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
        const { data } = ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);

        let totalLuminance = 0;
        let nearWhitePixels = 0;
        let visiblePixels = 0;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] < TRANSPARENT_ALPHA_THRESHOLD) continue;
          const luminance = (data[i] + data[i + 1] + data[i + 2]) / 3;
          totalLuminance += luminance;
          if (luminance >= WHITE_LUMINANCE_THRESHOLD) nearWhitePixels += 1;
          visiblePixels += 1;
        }

        if (visiblePixels > 0 && !cancelled) {
          const averageIsNearWhite = totalLuminance / visiblePixels >= WHITE_LUMINANCE_THRESHOLD;
          const hasSignificantNearWhitePortion =
            nearWhitePixels / visiblePixels >= NEAR_WHITE_PIXEL_FRACTION_THRESHOLD;
          setIsNearWhite(averageIsNearWhite || hasSignificantNearWhitePortion);
        }
      } catch {
        // Cross-origin canvas read blocked or otherwise unreadable — keep the original color.
      }
    };
    img.src = src;

    return () => {
      cancelled = true;
    };
  }, [src, skip]);

  return isNearWhite;
}

interface BrandLogoProps {
  customLogoUrl?: string | null;
  fallbackLogo: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  crossOrigin?: 'anonymous';
  /** Skips near-white detection and always renders the logo in its own colors inside a neutral
   * backdrop. Use for canvas/PDF-captured contexts (e.g. html2canvas) where the capture can
   * happen before async detection resolves. */
  preserveOriginalColor?: boolean;
}

/** A custom org logo is usually fine as-is, but occasionally it's white — invisible against these
 * headers' white background. Detect that case and tint only those logos to a fixed brand blue via
 * an SVG feComposite filter (recolors using the image's own rendered alpha, applied to the already
 * -loaded <img> so it needs no extra cross-origin resource fetch); every other custom logo, and the
 * default brand logo, renders unmodified in its real colors. */
const BrandLogo: React.FC<BrandLogoProps> = ({
  customLogoUrl,
  fallbackLogo,
  className,
  style,
  onClick,
  crossOrigin,
  preserveOriginalColor = false,
}) => {
  const filterId = useId();
  const isNearWhite = useIsNearWhiteLogo(customLogoUrl, preserveOriginalColor);

  if (!customLogoUrl) {
    return (
      <img src={fallbackLogo} className={className} style={style} alt="Logo" onClick={onClick} crossOrigin={crossOrigin} />
    );
  }

  if (preserveOriginalColor || !isNearWhite) {
    const img = (
      <img src={customLogoUrl} className={className} style={style} alt="Logo" onClick={onClick} crossOrigin={crossOrigin} />
    );
    return preserveOriginalColor ? <span className={styles.customLogoBackdrop}>{img}</span> : img;
  }

  return (
    <>
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true" focusable="false">
        <defs>
          <filter id={filterId}>
            <feFlood floodColor={TINT_COLOR} result="flood" />
            <feComposite in="flood" in2="SourceGraphic" operator="in" />
          </filter>
        </defs>
      </svg>
      <img
        src={customLogoUrl}
        className={className}
        style={{ ...style, filter: `url(#${filterId})` }}
        alt="Logo"
        onClick={onClick}
        crossOrigin={crossOrigin}
      />
    </>
  );
};

export default BrandLogo;
